// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';

const taxAnalystRoleMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.taxAnalyst.roleName',
    description: 'The name of the tax analyst role.',
    defaultMessage: 'Tax Analyst',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.taxAnalyst.shortDescription',
    description: 'The short description of the tax analyst role.',
    defaultMessage:
      'Can configure tax settings, file tax forms, and export data',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.taxAnalyst.descriptionOfWhatRoleCanDo',
    description: 'The description of what the tax analyst role can do.',
    defaultMessage:
      'This role is for people who need to configure tax form settings, file tax forms for connected accounts, and export data.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.taxAnalyst.descriptionOfWhatRoleCannotDo',
    description: "The description of what the tax analyst role can't do.",
    defaultMessage:
      "They can't create connected accounts, transfer funds, payout money, or edit account and non-Tax product settings.",
  },
});

const taxAnalystRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...taxAnalystRoleMessages.roleName} />,
  roleId: 'tax_analyst',
  isBeta: false,
  shortDescription: (
    <FormattedMessage {...taxAnalystRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...taxAnalystRoleMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage
      {...taxAnalystRoleMessages.descriptionOfWhatRoleCannotDo}
    />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.configureTaxAndTaxFormSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.configureTaxFormsForConnectedAccounts}
    />,
    <FormattedMessage
      {...actionsRolesAllow.fullAccessToTheTaxFormsView}
      values={{
        a: (content) => (
          <a href="https://dashboard.stripe.com/connect/tax_forms">{content}</a>
        ),
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.viewDisputes} />,
    <FormattedMessage {...actionsRolesAllow.viewPayments} />,
    <FormattedMessage {...actionsRolesAllow.viewProducts} />,
    <FormattedMessage {...actionsRolesAllow.viewCustomers} />,
    <FormattedMessage {...actionsRolesAllow.viewConnectedAccounts} />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.resolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createEditRejectConnectedAccounts}
    />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage
      {...actionsRolesAllow.editAnyProductSettingsBesidesTax}
    />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
  ],
};

export const allTaxRoleProps = [taxAnalystRoleDescriptionProps];
